'use client';

import Link from 'next/link';
import RewardCard from './rewards/RewardCard';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { rewardEntryLabel } from '@/lib/rewards';
import { fetchMarqHistory, type MarqEntry } from '@/lib/api';
import MarqCoin from './MarqCoin';
import { fetchAiPricing, quoteLevel, type AiPricing } from '@/lib/levels';
import { S_CARD, S_HEADING, S_MUTED } from './settingsUi';

/**
 * Settings ▸ Credit — the balance, the daily reward, and where it has gone.
 *
 * ── Why the history is here and not in the header popover ──────────────────
 * The reward panel answers one question ("can I claim, and when is the next
 * one"), and it is open for a few seconds at a time. "Why is my balance lower
 * than I expected" is a different question, asked while sitting still, and the
 * answer is a list. Putting a scrollable ledger inside a 64px-wide popover
 * would have made the quick question slower to answer without making the slow
 * one much faster.
 *
 * The list is fetched on mount rather than carried in the auth context, because
 * it is the one piece of KHARMA that no other screen wants and that would
 * otherwise be refetched on every page load for the few people who open it.
 */
/** Reference texts for the price list: one short sentence, and a full 5,000-character page. */
const SENTENCE = 'Where is the train station?';
const PAGE = 'x'.repeat(5000);

export default function CreditSettings() {
  const { marq } = useAuth();
  const [entries, setEntries] = useState<MarqEntry[] | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    // An empty list and a failed fetch look the same on purpose: there is
    // nothing a reader can do about either, and "could not load your history"
    // over a working balance would read as a fault in the balance.
    setError('');
    fetchMarqHistory(25).then(setEntries).catch(() => setError('Your activity could not be loaded. Please try again.'));
  }, []);

  useEffect(load, [load, marq?.balance]);

  // The engine's prices per translation level; null while unknown or when level pricing is off (the flat list then).
  const [pricing, setPricing] = useState<AiPricing | null>(null);
  useEffect(() => { fetchAiPricing().then(setPricing).catch(() => setPricing(null)); }, []);

  if (!marq) {
    return (
      <p className="text-sm text-ink-500 dark:text-ink-400">
        Credit is not available on this account.
      </p>
    );
  }

  return (
    <div className="space-y-5">
      {/* The balance, stated once and large. This is the thing the tab is for. */}
      <div className={'flex items-center gap-4 p-5 ' + S_CARD}>
        <MarqCoin className="h-10 w-10" />
        <div className="min-w-0 flex-1">
          <p className={'text-xs ' + S_MUTED}>Balance</p>
          <p className="text-3xl font-semibold leading-tight tracking-tight tabular-nums text-ink-900 dark:text-white">
            {marq.balance.toLocaleString()} <span className="text-sm font-medium text-ink-400 dark:text-ink-500">KHARMA</span>
          </p>
        </div>

      </div>

      <RewardCard compact />
      <Link href="/rewards" className="inline-flex text-sm font-semibold text-accent-600 underline underline-offset-4 dark:text-accent-400">Open Rewards & KHARMA →</Link>
      {error && <div role="alert" className="rounded-xl border border-red-300 p-3 text-sm text-red-700 dark:text-red-300">{error} <button type="button" onClick={load} className="underline">Retry</button></div>}

      <dl className={'grid grid-cols-2 divide-x divide-ink-200 text-sm dark:divide-white/10 ' + S_CARD}>
        <div className="px-5 py-4">
          <dt className={'text-xs ' + S_MUTED}>Earned</dt>
          <dd className="mt-0.5 text-lg font-semibold tabular-nums text-ink-900 dark:text-white">{marq.earned.toLocaleString()}</dd>
        </div>
        <div className="px-5 py-4">
          <dt className={'text-xs ' + S_MUTED}>Spent</dt>
          <dd className="mt-0.5 text-lg font-semibold tabular-nums text-ink-900 dark:text-white">{marq.spent.toLocaleString()}</dd>
        </div>
      </dl>

      <section>
        <h3 className={'mb-2 ' + S_HEADING}>
          What things cost
        </h3>
        <ul className={'divide-y divide-ink-200 text-sm text-ink-600 dark:divide-white/10 dark:text-ink-300 ' + S_CARD}>
          {pricing?.live ? (
            <>
              {pricing.levels.map((l) => (
                <Price key={l.key} what={`Translation · ${l.name}`} note="a sentence – a full page"
                       cost={`${quoteLevel(pricing, l, SENTENCE, false)}–${quoteLevel(pricing, l, PAGE, false).toLocaleString()}`} />
              ))}
              <Price what="Pronunciation" cost="1" note="free when already heard" />
              <Price what="Voice file" cost="By length" note="about 2 a minute" />
              <Price what="Reading an image" cost="By size" note="usually 1" />
            </>
          ) : (
            <>
              <Price what="Translation" cost="1" note="plus 1 for each AI extra" />
              <Price what="Pronunciation" cost="1" />
              <Price what="Voice file" cost="3" />
              <Price what="Reading an image" cost="3" />
            </>
          )}
          <Price what="Dictionary, language detection" cost="Free" />
        </ul>
      </section>

      <section>
        <h3 className={'mb-2 ' + S_HEADING}>
          Recent activity
        </h3>
        {entries === null ? (
          <p className="text-sm text-ink-400 dark:text-ink-500">Loading…</p>
        ) : entries.length === 0 ? (
          <p className="text-sm text-ink-400 dark:text-ink-500">Nothing yet.</p>
        ) : (
          <ul className={'divide-y divide-ink-200 dark:divide-white/10 ' + S_CARD}>
            {entries.map((e, i) => (
              <li key={i} className="flex items-baseline justify-between gap-3 px-4 py-2.5 text-sm">
                <span className="min-w-0 truncate text-ink-700 dark:text-ink-200">{rewardEntryLabel(e)}</span>
                <span className="shrink-0 tabular-nums text-ink-400 dark:text-ink-500">
                  {when(e.at)}
                </span>
                <span
                  className={
                    'shrink-0 w-16 text-right font-semibold tabular-nums ' +
                    (e.amount >= 0
                      ? 'text-ink-900 dark:text-white'
                      : 'text-ink-400 dark:text-ink-500')
                  }
                >
                  {e.amount >= 0 ? `+${e.amount}` : e.amount}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function Price({ what, cost, note }: { what: string; cost: string; note?: string }) {
  return (
    <li className="flex items-baseline justify-between gap-3 px-4 py-2.5">
      <span>
        {what}
        {note && <span className="text-ink-400 dark:text-ink-500"> — {note}</span>}
      </span>
      <span className="shrink-0 font-medium text-ink-900 dark:text-ink-50">{cost}</span>
    </li>
  );
}

/**
 * "3m", "5h", "2d" — a duration, not a timestamp.
 *
 * The question this list answers is "what did I just spend it on", and for that
 * the ordering and the recency are the whole content; an exact clock time would
 * be noise and would need a timezone to be honest about.
 */
function when(at: string): string {
  const ms = Date.now() - new Date(at).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}
